import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthenticatedFetchWithWorkspace } from '../Keycloak/useAuthenticatedFetchWithWorkspace';
import { useQueryBuilderCache } from '../../context/QueryBuilderCacheContext';
import useWorkspaceChange from '../useWorkspaceChange';
import { toErrorMessage } from './useRequestState';

export const useCachedQuery = <T,>(cacheKey: string, fetcher: () => Promise<T>, emptyValue: T, fallbackError: string) => {
    const { isReady } = useAuthenticatedFetchWithWorkspace();
    const cache = useQueryBuilderCache();
    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;
    const emptyRef = useRef(emptyValue);

    const [data, setData] = useState<T>(() => cache?.get<T>(cacheKey)?.data ?? emptyValue);
    const [loading, setLoading] = useState(() => !cache?.get<T>(cacheKey));
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async (forceRefresh = false) => {
        if (!isReady) return;
        const hit = forceRefresh ? undefined : cache?.get<T>(cacheKey);
        if (hit) {
            setData(hit.data);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const result = await fetcherRef.current();
            setData(result);
            cache?.set(cacheKey, result);
        } catch (err) {
            setError(toErrorMessage(err, fallbackError));
        } finally {
            setLoading(false);
        }
    }, [isReady, cache, cacheKey, fallbackError]);

    useWorkspaceChange(() => {
        setData(emptyRef.current);
        setLoading(true);
    });

    useEffect(() => {
        load();
    }, [load]);

    const refetch = useCallback(() => load(true), [load]);

    return { data, loading, error, refetch };
};
